// MCHD Room Scheduler - Floor Management Component
// Manages floors for a specific building

import React, { useState } from 'react';
import { Paper, Title, Group, Button, TextInput, Select, Text, Stack, Alert, Badge, ActionIcon, Menu, Card, SimpleGrid, Skeleton, Box } from '@mantine/core';
import { IconPlus, IconSearch, IconRefresh, IconDots, IconEdit, IconTrash, IconBuildingStore, IconHome } from '@tabler/icons-react';
import { useFloors, useCreateFloor, useUpdateFloor, useDeleteFloor } from '../../../hooks/coordinator/useBuildingManagement';
import { FloorForm } from './FloorForm';

interface Building {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
}

interface FloorData {
  id: string;
  number: number;
  name: string;
  buildingId: string;
  isActive: boolean;
  roomCount: number;
  createdAt: string;
  updatedAt: string;
}

interface FloorManagementProps {
  building: Building;
  onFloorSelect: (floor: FloorData) => void;
}

/**
 * Floor Management Component
 * Manages floors for a specific building
 */
export const FloorManagement: React.FC<FloorManagementProps> = ({
  building,
  onFloorSelect
}) => {
  const [selectedFloor, setSelectedFloor] = useState<FloorData | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const {
    data: floors,
    isLoading: loading,
    error,
    refetch
  } = useFloors(building.id);

  const createFloorMutation = useCreateFloor();
  const updateFloorMutation = useUpdateFloor();
  const deleteFloorMutation = useDeleteFloor();

  /**
   * Handle creating a new floor
   */
  const handleCreateFloor = () => {
    setSelectedFloor(null);
    setShowForm(true);
  };

  /**
   * Handle editing an existing floor
   */
  const handleEditFloor = (floor: FloorData) => {
    setSelectedFloor(floor);
    setShowForm(true);
  };

  /**
   * Handle floor form submission
   */
  const handleFormSubmit = async (floorData: any) => {
    try {
      if (selectedFloor) {
        await updateFloorMutation.mutateAsync({ id: selectedFloor.id, data: floorData });
      } else {
        await createFloorMutation.mutateAsync({ ...floorData, buildingId: building.id });
      }
      setShowForm(false);
      setSelectedFloor(null);
    } catch (error) {
      console.error('Error saving floor:', error);
    }
  };

  /**
   * Handle floor deletion
   */
  const handleDeleteFloor = async (floorId: string) => {
    try {
      await deleteFloorMutation.mutateAsync(floorId);
    } catch (error) {
      console.error('Error deleting floor:', error);
    }
  };

  /**
   * Handle floor selection for room management
   */
  const handleFloorSelect = (floor: FloorData) => {
    onFloorSelect(floor);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Stack gap="md">
      {/* Modern Header */}
      <Paper 
        p="lg" 
        withBorder 
        radius="lg"
        style={{ 
          background: 'white',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
          border: '1px solid #e2e8f0'
        }}
      >
        <Group justify="space-between" align="flex-start">
          <Group gap="lg">
            {/* Icon with MCHD Brand Colors */}
            <Box
              p="md"
              style={{
                background: 'linear-gradient(135deg, #28A8E0 0%, #1e8bc3 100%)',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(40, 168, 224, 0.15)'
              }}
            >
              <IconBuildingStore size={32} color="white" />
            </Box>
            
            {/* Title Section */}
            <div>
              <Title order={1} c="dark" fw={700} size="1.8rem" mb="xs">
                {building.name} - Floors
              </Title>
              <Text c="dimmed" size="md" fw={400} style={{ lineHeight: 1.5 }}>
                Organize rooms by floor level for efficient space management
              </Text>
              
              {/* Status Indicator */}
              <Group gap="xs" mt="sm">
                <Box
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#10b981'
                  }}
                />
                <Text size="sm" c="dimmed" fw={500}>
                  {floors?.length || 0} floors configured
                </Text>
              </Group>
            </div>
          </Group>
          
          {/* Action Buttons */}
          <Group gap="md">
            <Button
              variant="light"
              leftSection={<IconRefresh size={18} />}
              onClick={() => refetch()}
              loading={loading}
              radius="md"
              size="md"
              style={{ 
                border: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                color: '#64748b',
                fontWeight: 500
              }}
            >
              Refresh
            </Button>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={handleCreateFloor}
              radius="md"
              size="md"
              style={{
                background: 'linear-gradient(135deg, #28A8E0 0%, #1e8bc3 100%)',
                border: 'none',
                fontWeight: 600,
                fontSize: '14px'
              }}
            >
              Add Floor
            </Button>
          </Group>
        </Group>
      </Paper>

        {/* Error Alert */}
        {error && (
          <Alert color="red" title="Error">
            {error instanceof Error ? error.message : String(error)}
          </Alert>
        )}

      {/* Modern Filters */}
      <Paper 
        p="lg" 
        withBorder 
        radius="lg"
        style={{ 
          background: 'white',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
          border: '1px solid #e2e8f0'
        }}
      >
        <Group gap="lg" align="end">
          <TextInput
            placeholder="Search floors by number, name..."
            leftSection={<IconSearch size={18} color="#64748b" />}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.currentTarget.value)}
            style={{ flex: 1 }}
            radius="md"
            size="md"
            styles={{
              input: {
                border: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                fontSize: '14px',
                height: '44px',
                '&:focus': {
                  borderColor: '#28A8E0',
                  backgroundColor: 'white',
                  boxShadow: '0 0 0 3px rgba(40, 168, 224, 0.1)'
                },
                '&::placeholder': {
                  color: '#94a3b8'
                }
              }
            }}
          />
          <Select
            placeholder="All Status"
            data={[
              { value: '', label: 'All Status' },
              { value: 'active', label: 'Active Only' },
              { value: 'inactive', label: 'Inactive Only' }
            ]}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value || '')}
            clearable
            radius="md"
            size="md"
            styles={{
              input: {
                border: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                fontSize: '14px',
                height: '44px',
                '&:focus': {
                  borderColor: '#28A8E0',
                  backgroundColor: 'white',
                  boxShadow: '0 0 0 3px rgba(40, 168, 224, 0.1)'
                }
              }
            }}
          />
        </Group>
      </Paper>

      {/* Floors Card Grid */}
      <Paper withBorder radius="md" p={0}>
        {loading ? (
          <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, md: 4 }} spacing="md" p="lg">
            {Array.from({ length: 8 }).map((_, index) => (
              <Card key={index} shadow="sm" radius="md" p="lg">
                <Stack gap="md">
                  <Group justify="center">
                    <Skeleton height={40} width={40} radius="md" />
                  </Group>
                  <Skeleton height={20} width="80%" mx="auto" />
                  <Skeleton height={16} width="60%" mx="auto" />
                  <Group justify="space-between">
                    <Skeleton height={24} width={60} />
                    <Skeleton height={32} width={32} circle />
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        ) : !Array.isArray(floors) || floors.length === 0 ? (
          <Stack align="center" gap="lg" py="xl">
            <IconBuildingStore size={64} color="var(--mantine-color-gray-4)" />
            <Text size="lg" fw={500} c="dimmed">
              No floors in {building.name} yet
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Add floors to organize rooms by level
            </Text>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleCreateFloor}
              style={{
                backgroundColor: '#28A8E0',
                border: 'none'
              }}
            >
              Add Floor
            </Button>
          </Stack>
        ) : (
          <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, md: 4 }} spacing="md" p="lg">
            {(Array.isArray(floors) ? floors : []).map((floor) => (
              <Card 
                key={floor.id}
                shadow="sm" 
                radius="md" 
                p="lg"
                style={{
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  border: '1px solid transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.borderColor = '#28A8E0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <Stack gap="md" align="center">
                  {/* Floor Number Badge */}
                  <Badge
                    size="xl"
                    variant="filled"
                    color="blue"
                    style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      padding: '12px 16px',
                      borderRadius: '12px',
                      minWidth: '60px'
                    }}
                  >
                    {floor.number}
                  </Badge>

                  {/* Floor Name */}
                  <Text size="md" fw={600} ta="center">
                    {floor.name}
                  </Text>

                  {/* Room Count */}
                  <Group gap="xs" align="center">
                    <IconHome size={16} color="#28A8E0" />
                    <Text size="lg" fw={700} c="dark">
                      {floor.roomCount || 0}
                    </Text>
                    <Text size="sm" c="dimmed">
                      Rooms
                    </Text>
                  </Group>

                  {/* Status Badge */}
                  <Badge
                    color={floor.isActive ? 'green' : 'red'}
                    variant="light"
                    size="sm"
                  >
                    {floor.isActive ? 'Active' : 'Inactive'}
                  </Badge>

                  {/* Actions */}
                  <Group justify="center" gap="xs">
                    <Button
                      size="sm"
                      leftSection={<IconHome size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFloorSelect(floor as FloorData);
                      }}
                      style={{
                        backgroundColor: '#28A8E0',
                        border: 'none'
                      }}
                    >
                      Manage Rooms
                    </Button>
                    
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <ActionIcon 
                          variant="subtle" 
                          c="gray"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditFloor(floor as FloorData);
                          }}
                        >
                          Edit Floor
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          c="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFloor(floor.id);
                          }}
                        >
                          Delete Floor
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>

                  {/* Created Date */}
                  <Text size="xs" c="dimmed" ta="center">
                    Created: {formatDate(floor.createdAt)}
                  </Text>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Paper>

      {/* Floor Form Modal */}
      {showForm && (
        <FloorForm
          floor={selectedFloor}
          buildingId={building.id}
          onSubmit={handleFormSubmit}
          onClose={() => {
            setShowForm(false);
            setSelectedFloor(null);
          }}
        />
      )}
    </Stack>
  );
};
